import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';
import { validateMediaUrl } from '../utils/mediaUrlUtils';
import SupportedPlatformsModal from './SupportedPlatformsModal';

export const CreateCampaign = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetLink: '',
    category: 'environment',
    mediaUrl: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urlValidation, setUrlValidation] = useState({ isValid: true, error: null });
  const [showPlatformsModal, setShowPlatformsModal] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Campaign categories
  const categories = [
    { id: 'environment', name: 'Environment' },
    { id: 'education', name: 'Education' },
    { id: 'health', name: 'Health' },
    { id: 'community', name: 'Community' },
    { id: 'technology', name: 'Technology' },
    { id: 'arts', name: 'Arts & Culture' },
    { id: 'business', name: 'Business' },
    { id: 'social', name: 'Social Impact' },
    { id: 'food', name: 'Food' },
    { id: 'creatives', name: 'Creatives' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user changes input
    if (error) setError(null);
    
    // Validate URL if mediaUrl is being changed
    if (name === 'mediaUrl' && value) {
      const validation = validateMediaUrl(value);
      setUrlValidation({
        isValid: validation.isValid,
        error: validation.error
      });
    } else if (name === 'mediaUrl' && !value) {
      // Clear validation if URL is empty
      setUrlValidation({ isValid: true, error: null });
    }
  };

  const handleUrlBlur = () => {
    if (formData.mediaUrl) {
      const validation = validateMediaUrl(formData.mediaUrl);
      setUrlValidation({
        isValid: validation.isValid,
        error: validation.error
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to create a campaign');
      return;
    }
    
    // Validate media URL if provided
    if (formData.mediaUrl) {
      const validation = validateMediaUrl(formData.mediaUrl);
      if (!validation.isValid) {
        setUrlValidation({
          isValid: false,
          error: validation.error
        });
        return;
      }
    }
    
    setIsLoading(true);
    
    try {
      const response = await campaignService.createCampaign(formData);
      
      // The API returns the campaign ID directly
      if (response && response.id) {
        navigate(`/campaigns/${response.id}`);
      } else {
        setError('Failed to create campaign');
      }
    } catch (err) {
      setError('An error occurred while creating the campaign');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full p-4 overflow-hidden flex flex-col main-container" style={{ backgroundColor: 'transparent' }}>
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg p-4 h-full">
          <div className="create-campaign__header">
            <div className="create-campaign__icon">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-brown-800 text-center">
              Create a New Campaign
            </h2>
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label htmlFor="title" className="block text-brown-700 text-xs font-bold mb-1">
                  Campaign Title
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 text-sm leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter a catchy title"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="category" className="block text-brown-700 text-xs font-bold mb-1">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 text-sm leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300 bg-white"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="block text-brown-700 text-xs font-bold mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 text-sm leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your campaign in detail"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label htmlFor="mediaUrl" className="block text-brown-700 text-xs font-bold mb-1">
                  Media URL (Optional)
                </label>
                <input
                  type="url"
                  id="mediaUrl"
                  name="mediaUrl"
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 text-sm leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300 ${
                    !urlValidation.isValid ? 'border-red-500' : ''
                  }`}
                  value={formData.mediaUrl}
                  onChange={handleChange}
                  onBlur={handleUrlBlur}
                  placeholder="https://www.youtube.com/watch?v=example"
                />
                
                {!urlValidation.isValid && (
                  <div className="text-red-500 text-xs mt-1">
                    {urlValidation.error}
                  </div>
                )}
                
                {formData.mediaUrl && urlValidation.isValid && (
                  <div className="text-green-600 text-xs mt-1">
                    ✓ Valid URL from {validateMediaUrl(formData.mediaUrl).platform.name}
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="targetLink" className="block text-brown-700 text-xs font-bold mb-1">
                  Target Link (Optional)
                </label>
                <input
                  type="url"
                  id="targetLink"
                  name="targetLink"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-brown-700 text-sm leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.targetLink}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>
            
            <div className="mb-4 flex items-center justify-between bg-brown-50 rounded p-2">
              <div>
                <h3 className="text-xs font-bold text-brown-700">Supported Platforms</h3>
                <p className="text-xs text-brown-600">YouTube, Google Drive, Instagram, TikTok</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPlatformsModal(true)}
                className="bg-green-100 hover:bg-green-200 text-green-800 font-medium py-1 px-2 rounded text-xs transition-colors duration-200 flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                View
              </button>
            </div>
            
            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={() => navigate('/campaigns')}
                className="bg-brown-300 hover:bg-brown-400 text-brown-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition-colors duration-300 text-sm"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                className={`bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition-colors duration-300 text-sm ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <SupportedPlatformsModal
        isOpen={showPlatformsModal}
        onClose={() => setShowPlatformsModal(false)}
      />
    </div>
  );
};

export default CreateCampaign;
